import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { RefundInitiator, RefundStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, enumOptions, SelectFilterField } from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { Amount, formatCryptoAmount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { REFUND_STATUS_COLOR } from "@/lib/status-colors";
import { useTableState, type TableSearch } from "@/lib/table-state";
import { exportCsv } from "@/lib/csv";
import { formatStatusLabel } from "@/lib/utils";
import { listRefunds } from "@/api/refunds";
import { listMerchants } from "@/api/merchants";
import { type ListRefundsParams, type RefundListItem } from "@/api/types";

// Real implementation of Task 27 Part B, replacing the Task-22 thin
// placeholder: refund requests across all merchants (INVOICES_VIEWER-gated,
// same as _app.invoices.tsx). `GET /v1/refunds` genuinely supports
// server-side pagination + filters (no configurable sort — fixed
// `createdAt DESC, id DESC`), so this follows Task 26's invoices precedent:
// real `useTableState` + `DataTable`, URL-persisted filters/page via
// `validateSearch`. No sortable columns and no `onSortChange` wiring — there
// is nothing on the backend to sort by. Read-only: no row detail page exists
// for a refund (a refund's full context is already surfaced within the
// invoice 360° page's Refunds section, Task 26), so rows are otherwise plain
// and non-interactive — except the Invoice ID cell, which links to that
// invoice's own detail page.
// A `type` alias (not `interface`) — see _app.invoices.tsx's identical note:
// `useTableState<TFilters>`'s constraint is `Record<string, unknown>`, which
// only a type alias's implicit index signature satisfies.
type RefundFilters = {
  status?: RefundStatus;
  initiator?: RefundInitiator;
  merchantId?: string;
};

const DEFAULT_FILTERS: RefundFilters = {};

/** Plain type-guarded passthrough — mirrors _app.invoices.tsx's `validateSearch` house style. */
function validateRefundsSearch(search: Record<string, unknown>): TableSearch<RefundFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    status: typeof search.status === "string" ? (search.status as RefundStatus) : undefined,
    initiator: typeof search.initiator === "string" ? (search.initiator as RefundInitiator) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
  };
}

export const Route = createFileRoute("/_app/refunds")({
  validateSearch: validateRefundsSearch,
  component: RefundsPage,
});

// ─── formatting helpers (shared by table cells and CSV export) ─────────────

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

// ─── refunds page ────────────────────────────────────────────────────────────

function RefundsPage() {
  const tableState = useTableState<RefundFilters>({ defaultFilters: DEFAULT_FILTERS });

  const params: ListRefundsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["refunds", params],
    queryFn: () => listRefunds(params),
  });

  // Merchant filter is a name dropdown (not a raw UUID input) — reuses
  // listMerchants, same as _app.invoices.tsx.
  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) =>
    a.businessName.localeCompare(b.businessName),
  );

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<RefundListItem>[] = [
    {
      key: "invoicePublicId",
      header: "Invoice ID",
      cell: (r) => (
        <Link to="/invoices/$invoiceId" params={{ invoiceId: r.invoiceId }} className="group -m-2 block p-2">
          <span className="group-hover:underline">{r.invoicePublicId}</span>
        </Link>
      ),
    },
    {
      // feature-037: an invoice's own refund, or one of a deposit sent after it closed.
      key: "owner",
      header: "Owner",
      cell: (r) =>
        r.unattachedDeposit ? (
          <Link
            to="/unattached-deposits/$unattachedDepositId"
            params={{ unattachedDepositId: r.unattachedDeposit.id }}
            className="font-mono text-xs hover:underline"
          >
            Unattached {r.unattachedDeposit.publicId}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">Invoice</span>
        ),
    },
    {
      key: "merchant",
      header: "Merchant",
      filter: columnFilter(tableState, ["merchantId"], (draft, set) => (
        <SelectFilterField
          id="filter-merchant"
          label="Merchant"
          allLabel="All merchants"
          options={sortedMerchants.map((m) => ({ value: m.id, label: m.businessName }))}
          value={draft.merchantId}
          onChange={(v) => set("merchantId", v)}
        />
      )),
      cell: (r) => (
        <MerchantCell merchantId={r.merchantId} businessName={r.merchantBusinessName} publicId={r.merchantPublicId} />
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (r) => <AssetLabel symbol={r.assetSymbol} network={r.assetNetwork} />,
    },
    {
      key: "status",
      header: "Status",
      filter: columnFilter(tableState, ["status"], (draft, set) => (
        <SelectFilterField
          id="filter-status"
          label="Status"
          allLabel="All statuses"
          options={enumOptions(Object.values(RefundStatus))}
          value={draft.status}
          onChange={(v) => set("status", v as RefundStatus | undefined)}
        />
      )),
      cell: (r) => <StatusBadge label={formatStatusLabel(r.status)} color={REFUND_STATUS_COLOR[r.status]} />,
    },
    {
      key: "initiator",
      header: "Initiator",
      filter: columnFilter(tableState, ["initiator"], (draft, set) => (
        <SelectFilterField
          id="filter-initiator"
          label="Initiator"
          allLabel="All initiators"
          options={enumOptions(Object.values(RefundInitiator))}
          value={draft.initiator}
          onChange={(v) => set("initiator", v as RefundInitiator | undefined)}
        />
      )),
      cell: (r) => formatStatusLabel(r.initiator),
    },
    {
      key: "claimant",
      header: "Claimant",
      cell: (r) => (r.claimant ? formatStatusLabel(r.claimant) : "—"),
    },
    {
      key: "amount",
      header: "Amount",
      className: "text-right",
      cell: (r) => <Amount native={r.amount} symbol={r.assetSymbol} usd={r.amountUsd} />,
    },
    {
      key: "address",
      header: "Address",
      cell: (r) => <HashLink value={r.address} type="address" network={r.assetNetwork} />,
    },
    {
      key: "claimedAt",
      header: "Claimed",
      cell: (r) => formatTimestamp(r.claimedAt),
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        loading={isPending}
        error={isError ? "Failed to load refunds." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        csvExport={{
          onExport: () =>
            exportCsv({
              columns: [
                { header: "Invoice ID", value: (r: RefundListItem) => r.invoicePublicId },
                { header: "Merchant", value: (r: RefundListItem) => r.merchantBusinessName },
                { header: "Asset", value: (r: RefundListItem) => r.assetSymbol },
                { header: "Network", value: (r: RefundListItem) => r.assetNetwork },
                { header: "Status", value: (r: RefundListItem) => r.status },
                { header: "Initiator", value: (r: RefundListItem) => r.initiator },
                { header: "Claimant", value: (r: RefundListItem) => r.claimant ?? "" },
                {
                  header: "Amount",
                  value: (r: RefundListItem) => formatCryptoAmount(r.amount, r.assetSymbol),
                },
                { header: "Address", value: (r: RefundListItem) => r.address ?? "" },
                { header: "Claimed", value: (r: RefundListItem) => formatTimestamp(r.claimedAt) },
                { header: "Refund declines", value: (r: RefundListItem) => String(r.refundDeclineCount) },
                {
                  header: "Gas-fund declines",
                  value: (r: RefundListItem) => String(r.gasFundDeclineCount),
                },
                { header: "Created", value: (r: RefundListItem) => formatTimestamp(r.createdAt) },
              ],
              fetchPage: (page, pageSize) =>
                listRefunds({ ...tableState.filters, page, pageSize }).then((r) => ({
                  data: r.data,
                  total: r.total,
                })),
              filenamePrefix: "refunds",
            }),
        }}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(r) => r.id}
        emptyMessage="No refunds match your filters."
      />
    </div>
  );
}
